import React, { memo, useCallback, useEffect, useRef, useState } from 'react'

import { Notice, Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

import arrow from 'images/three_d_studio/model_ready_toaster/arrow.webp'

const DISMISSED_KEY = 'modelReadyToasterDismissed'

const isDismissed = () => {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === '1'
  } catch (_) {
    return false
  }
}

const dismiss = () => {
  try {
    window.localStorage.setItem(DISMISSED_KEY, '1')
  } catch (_) {
    // Storage may be unavailable (private mode, quota). Treat as no-op.
  }
}

function ModelReadyToaster() {
  const t = useTranslation()
  const hasGenerated = useThreeDStudioStore(state => state.generation.hasGenerated)
  const openedModelId = useThreeDStudioStore(state => state.ui.openedModel?.id)
  const isViewerSidebarOpen = useThreeDStudioStore(state => state.ui?.isViewerSidebarOpen)
  const prevHasGeneratedRef = useRef(hasGenerated)
  const prevOpenedModelIdRef = useRef(openedModelId)
  const prevIsViewerSidebarOpenRef = useRef(isViewerSidebarOpen)
  const [isVisible, setIsVisible] = useState(false)

  const handleDontShowAgain = useCallback(() => {
    dismiss()
    setIsVisible(false)
  }, [])

  useEffect(() => {
    const justSucceeded = !prevHasGeneratedRef.current && hasGenerated
    if (justSucceeded && !isDismissed()) {
      setIsVisible(true)
    }
    prevHasGeneratedRef.current = hasGenerated
  }, [hasGenerated])

  useEffect(() => {
    if (!prevIsViewerSidebarOpenRef.current && isViewerSidebarOpen) {
      handleDontShowAgain()
    } else if (prevOpenedModelIdRef.current !== openedModelId) {
      setIsVisible(false)
    }
    prevOpenedModelIdRef.current = openedModelId
    prevIsViewerSidebarOpenRef.current = isViewerSidebarOpen
  }, [openedModelId, isViewerSidebarOpen, handleDontShowAgain])

  if (!isVisible) return null

  return (
    <div className="model-ready-toaster" role="status" aria-live="polite">
      <Notice useSSRIcon onClose={handleDontShowAgain}>
        <div className="model-ready-toaster__body">
          <p className="cgt-heading-50 model-ready-toaster__title">
            {t('viewer.modelReadyToaster.message')}
          </p>
          <Button
            type="ghost"
            size="small"
            onClick={handleDontShowAgain}
            customClass="model-ready-toaster__dismiss"
          >
            {t('viewer.modelReadyToaster.dontShowAgain')}
          </Button>
        </div>
        <img
          src={arrow}
          alt=""
          aria-hidden="true"
          className="model-ready-toaster__flourish"
          width={90}
          height={97}
        />
      </Notice>
    </div>
  )
}

export default memo(ModelReadyToaster)
